import {
  getStoredVisitorId,
  completePlaidOAuthRedirect,
  openPlaidLink,
  getAccounts,
  getActivities,
  getHoldings,
  getCostBasisOverrides,
  saveCostBasisOverride,
  type CostBasisOverride,
} from './plaidApi.ts';
import { getOptionQuotes, type OptionQuote } from '../../api/massiveApi.ts';
import type {
  AccountActivity,
  AccountHolding,
  ConnectionState,
  TradeMatchingResult,
  HoldingsStrategy,
} from './brokerage.types.ts';
import { formatPrice, formatPriceWithCents, formatDate } from '../../shared/formatting.ts';
import { showLoading, showError, setContent, makeTable } from '../../shared/ui.ts';
import { matchTrades, groupIntoStrategies } from './tradeMatching.ts';
import { groupHoldingsIntoStrategies } from './holdingsStrategy.ts';
import type { OptionsStrategy } from './brokerage.types.ts';

const CONTAINER_ID = 'brokerage-result';
const ACCOUNT_NAMES_KEY = 'dyr_account_names';
const SELECTED_ACCOUNT_KEY = 'dyr_selected_account';

function domId(value: string): string {
  return value.replace(/[^a-zA-Z0-9-]/g, '_');
}

function jsString(value: string): string {
  return JSON.stringify(value).replace(/"/g, '&quot;');
}

// Account selection persistence
function getStoredSelectedAccount(): string | null {
  return localStorage.getItem(SELECTED_ACCOUNT_KEY);
}

function setStoredSelectedAccount(accountId: string): void {
  localStorage.setItem(SELECTED_ACCOUNT_KEY, accountId);
}

// Account naming helpers
function getAccountNames(): Record<string, string> {
  try {
    const stored = localStorage.getItem(ACCOUNT_NAMES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function setAccountName(accountId: string, name: string): void {
  const names = getAccountNames();
  names[accountId] = name;
  localStorage.setItem(ACCOUNT_NAMES_KEY, JSON.stringify(names));
}

function getAccountDisplayName(accountId: string, defaultName: string): string {
  const names = getAccountNames();
  return names[accountId] || defaultName;
}

let connectionState: ConnectionState = {
  visitorId: null,
  isConnected: false,
  accounts: [],
  selectedAccountId: null,
};

// Cache for filter functionality
let cachedHoldings: AccountHolding[] = [];
let cachedActivities: AccountActivity[] = [];

// Cost basis edit modal state
let costBasisModalActivity: AccountActivity | null = null;
let currentCostBasisOverrides: Map<string, CostBasisOverride> = new Map();

function getPnlDateRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date(end);
  start.setFullYear(start.getFullYear() - 5);

  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
}

export async function initializeBrokerage(): Promise<void> {
  const visitorId = getStoredVisitorId();
  if (visitorId) {
    connectionState.visitorId = visitorId;
  }

  try {
    const oauthResult = await completePlaidOAuthRedirect();
    if (oauthResult) {
      console.log('Plaid OAuth redirect completed:', oauthResult);
      connectionState.visitorId = getStoredVisitorId();
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown Plaid OAuth error';
    showError(CONTAINER_ID, message);
    return;
  }

  if (connectionState.visitorId) {
    await refreshAccounts();
  }

  renderBrokerageUI();

  // Auto-load holdings if we have a connected account
  if (connectionState.isConnected && connectionState.selectedAccountId) {
    await loadHoldings();
  }
}

export async function connectBrokerage(): Promise<void> {
  showLoading(CONTAINER_ID);

  try {
    // Plaid Link opens as an in-page modal
    setContent(CONTAINER_ID, `
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p class="font-medium mb-2">Opening Plaid Link...</p>
        <p>Select your brokerage and log in to connect your account.</p>
      </div>
    `);

    const result = await openPlaidLink();

    // Store visitor ID if not already stored
    if (!connectionState.visitorId) {
      const visitorId = getStoredVisitorId();
      if (visitorId) {
        connectionState.visitorId = visitorId;
      }
    }

    console.log('Plaid Link success:', result);

    // Refresh accounts and show connected state
    await refreshAccounts();
    renderBrokerageUI();

    // Auto-load holdings for newly connected account
    if (connectionState.isConnected && connectionState.selectedAccountId) {
      await loadHoldings();
    }

  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    // Only show error if it's not just the user closing the modal
    if (!message.includes('closed by user')) {
      showError(CONTAINER_ID, message);
    } else {
      // User cancelled - restore previous UI state
      renderBrokerageUI();
    }
  }
}

export async function refreshBrokerageAccounts(): Promise<void> {
  showLoading(CONTAINER_ID);
  await refreshAccounts();
  renderBrokerageUI();

  // Auto-load holdings after refreshing accounts
  if (connectionState.isConnected && connectionState.selectedAccountId) {
    await loadHoldings();
  }
}

async function refreshAccounts(): Promise<void> {
  if (!connectionState.visitorId) return;

  try {
    const accounts = await getAccounts(connectionState.visitorId);
    connectionState.accounts = accounts;
    connectionState.isConnected = accounts.length > 0;

    if (accounts.length > 0) {
      // Restore previously selected account from localStorage, or default to first
      const storedAccountId = getStoredSelectedAccount();
      const accountExists = accounts.some(a => a.id === storedAccountId);

      if (storedAccountId && accountExists) {
        connectionState.selectedAccountId = storedAccountId;
      } else if (!connectionState.selectedAccountId) {
        connectionState.selectedAccountId = accounts[0].id;
        setStoredSelectedAccount(accounts[0].id);
      }
    }

    document.dispatchEvent(new CustomEvent('dyr:brokerage-accounts-changed'));
  } catch (error) {
    console.error('Failed to refresh accounts:', error);
    connectionState.accounts = [];
    connectionState.isConnected = false;
  }
}

export async function selectAccount(accountId: string): Promise<void> {
  const account = connectionState.accounts.find(a => a.id === accountId);
  console.log(`🔄 Switching to account: ${account?.name} (#${account?.number}) - ID: ${accountId}`);

  connectionState.selectedAccountId = accountId;
  setStoredSelectedAccount(accountId);
  renderBrokerageUI();
  // Auto-load holdings for the newly selected account
  await loadHoldings();
}

export function promptRenameAccount(): void {
  const accountId = connectionState.selectedAccountId;
  if (!accountId) return;

  const account = connectionState.accounts.find(a => a.id === accountId);
  if (!account) return;

  const defaultName = `${account.institutionName} - ${account.name}`;
  const currentName = getAccountDisplayName(accountId, defaultName);

  const newName = prompt('Enter a name for this account:', currentName);
  if (newName !== null && newName.trim() !== '') {
    setAccountName(accountId, newName.trim());
    renderBrokerageUI();
  }
}

export async function loadActivities(): Promise<void> {
  if (!connectionState.visitorId || !connectionState.selectedAccountId) return;

  const activitiesContainer = document.getElementById('activities-container');
  if (activitiesContainer) {
    activitiesContainer.innerHTML = '<div class="text-gray-400 text-sm">Loading activities...</div>';
  }

  try {
    const startDateInput = document.getElementById('activities-start-date') as HTMLInputElement;
    const endDateInput = document.getElementById('activities-end-date') as HTMLInputElement;

    const dateRange = startDateInput?.value || endDateInput?.value
      ? {
          startDate: startDateInput?.value || '',
          endDate: endDateInput?.value || '',
        }
      : undefined;

    const activities = await getActivities(
      connectionState.visitorId,
      connectionState.selectedAccountId,
      dateRange
    );

    cachedActivities = activities;
    applyActivitiesFilter();
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    if (activitiesContainer) {
      activitiesContainer.innerHTML = `<div class="text-red-600 text-sm">${message}</div>`;
    }
  }
}

// Cache for live quotes
let cachedLiveQuotes: Map<string, OptionQuote> = new Map();

export async function loadHoldings(): Promise<void> {
  if (!connectionState.visitorId || !connectionState.selectedAccountId) return;

  const holdingsContainer = document.getElementById('holdings-container');
  if (holdingsContainer) {
    holdingsContainer.innerHTML = '<div class="text-gray-400 text-sm">Loading holdings...</div>';
  }

  try {
    const holdings = await getHoldings(
      connectionState.visitorId,
      connectionState.selectedAccountId
    );

    // DEBUG: Log raw holdings data
    console.group('🔍 DEBUG: Holdings (Raw)');
    console.log('Total holdings:', holdings.length);
    holdings.forEach((h, i) => {
      console.group(`Holding ${i + 1}: ${h.optionSymbol || h.symbol}`);
      console.log('Raw holding object:', h);
      console.log('Key fields:');
      console.log('  - symbol:', h.symbol);
      console.log('  - optionSymbol:', h.optionSymbol);
      console.log('  - units:', h.units);
      console.log('  - averagePurchasePrice:', h.averagePurchasePrice);
      console.log('  - currentPrice:', h.currentPrice);
      console.log('  - marketValue:', h.marketValue);
      console.log('  - unrealizedPnl:', h.unrealizedPnl);
      console.log('  - isOption:', h.isOption);
      console.groupEnd();
    });
    console.groupEnd();

    // Get live option prices (FMV) from Massive API
    const optionSymbols = holdings
      .filter(h => h.isOption && h.optionSymbol)
      .map(h => h.optionSymbol as string);

    if (optionSymbols.length > 0 && holdingsContainer) {
      holdingsContainer.innerHTML = '<div class="text-gray-400 text-sm">Loading live prices from Massive API...</div>';

      try {
        cachedLiveQuotes = await getOptionQuotes(optionSymbols);

        // DEBUG: Log Massive API quotes
        console.group('🔍 DEBUG: Massive API Quotes (FMV)');
        console.log('Requested symbols:', optionSymbols);
        console.log('Received quotes:', cachedLiveQuotes.size);
        cachedLiveQuotes.forEach((quote, symbol) => {
          console.log(`  ${symbol}:`, {
            fmv: quote.fmv,
            close: quote.close,
            underlying: quote.underlying,
            strike: quote.strike,
            expiration: quote.expiration,
            type: quote.type,
          });
        });
        console.groupEnd();
      } catch (error) {
        console.warn('Failed to fetch live option quotes:', error);
        cachedLiveQuotes = new Map();
      }
    }

    cachedHoldings = holdings;
    applyHoldingsFilter();
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    if (holdingsContainer) {
      holdingsContainer.innerHTML = `<div class="text-red-600 text-sm">${message}</div>`;
    }
  }
}

// Filter functions
export function applyHoldingsFilter(): void {
  const showOptions = (document.getElementById('holdings-filter-options') as HTMLInputElement)?.checked ?? true;
  const showStocks = (document.getElementById('holdings-filter-stocks') as HTMLInputElement)?.checked ?? true;

  console.log(`🔎 applyHoldingsFilter: cachedHoldings=${cachedHoldings.length}, showOptions=${showOptions}, showStocks=${showStocks}`);

  const filtered = cachedHoldings.filter(h => {
    if (h.isOption && !showOptions) return false;
    if (!h.isOption && !showStocks) return false;
    return true;
  });

  console.log(`🔎 After filter: ${filtered.length} holdings`);

  // Group into strategies and render with expandable panels
  const strategies = groupHoldingsIntoStrategies(filtered, cachedLiveQuotes);
  console.log(`🔎 After grouping: ${strategies.length} strategies`);
  renderHoldingsStrategies(strategies);
}

// Toggle strategy expansion
export function toggleStrategy(strategyId: string): void {
  const content = document.getElementById(`strategy-content-${strategyId}`);
  const arrow = document.getElementById(`strategy-arrow-${strategyId}`);
  if (content && arrow) {
    content.classList.toggle('hidden');
    arrow.classList.toggle('rotate-90');
  }
}

// Debug: Log cached holdings data
export function debugHoldings(): void {
  console.group('🔍 DEBUG: Holdings Data');
  console.log('Cached holdings count:', cachedHoldings.length);
  cachedHoldings.forEach((holding, i) => {
    console.group(`Holding ${i + 1}`);
    console.log('Full object:', holding);
    console.groupEnd();
  });
  console.groupEnd();
}

export function applyActivitiesFilter(): void {
  const showTrades = (document.getElementById('activities-filter-trades') as HTMLInputElement)?.checked ?? true;
  const showDividends = (document.getElementById('activities-filter-dividends') as HTMLInputElement)?.checked ?? false;
  const showInterest = (document.getElementById('activities-filter-interest') as HTMLInputElement)?.checked ?? false;
  const showOther = (document.getElementById('activities-filter-other') as HTMLInputElement)?.checked ?? false;

  const tradeTypes = ['BUY', 'SELL', 'BUY_TO_OPEN', 'BUY_TO_CLOSE', 'SELL_TO_OPEN', 'SELL_TO_CLOSE'];
  const dividendTypes = ['DIVIDEND', 'DIV'];
  const interestTypes = ['INTEREST', 'INT'];

  const filtered = cachedActivities.filter(a => {
    const type = (a.optionType || a.type || '').toUpperCase();

    // Check if it's a trade
    if (tradeTypes.some(t => type.includes(t))) {
      return showTrades;
    }

    // Check if it's a dividend
    if (dividendTypes.some(t => type.includes(t))) {
      return showDividends;
    }

    // Check if it's interest
    if (interestTypes.some(t => type.includes(t))) {
      return showInterest;
    }

    // Everything else is "other"
    return showOther;
  });

  renderActivitiesTable(filtered);
}

function renderBrokerageUI(): void {
  if (!connectionState.isConnected) {
    renderDisconnectedState();
    return;
  }

  renderConnectedState();
}

function renderDisconnectedState(): void {
  setContent(CONTAINER_ID, `
    <div class="bg-white rounded-lg border border-gray-200 p-6 max-w-md">
      <h3 class="text-lg font-medium mb-2">Connect Your Brokerage</h3>
      <p class="text-sm text-gray-500 mb-4">
        Link your brokerage account to view your positions and transaction history.
      </p>
      <button onclick="connectBrokerage()" class="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors">
        Connect Brokerage
      </button>
    </div>
  `);
}

function renderConnectedState(): void {
  const accountOptions = connectionState.accounts
    .map(account => {
      const selected = account.id === connectionState.selectedAccountId ? 'selected' : '';
      const defaultName = `${account.institutionName} - ${account.name}`;
      const displayName = getAccountDisplayName(account.id, defaultName);
      const statusDot = account.syncStatus === 'OK' ? '●' : '○';
      return `<option value="${account.id}" ${selected}>${statusDot} ${displayName}</option>`;
    })
    .join('');

  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  setContent(CONTAINER_ID, `
    <div class="space-y-6">
      <!-- Account Selector -->
      <div class="flex items-center gap-2">
        <select id="account-selector" onchange="selectAccount(this.value)"
          class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900">
          ${accountOptions}
        </select>
        <button onclick="promptRenameAccount()" class="p-2 text-gray-400 hover:text-gray-600" title="Rename account">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
          </svg>
        </button>
        <button onclick="connectBrokerage()" class="text-sm text-gray-500 hover:text-gray-700 ml-2">
          + Add account
        </button>
      </div>

      <!-- Tabs for Holdings/Activities/P&L -->
      <div class="border-b border-gray-200">
        <nav class="flex gap-4">
          <button onclick="showBrokerageTab('holdings')" id="tab-btn-holdings"
            class="py-2 px-1 border-b-2 border-gray-900 text-sm font-medium">
            Holdings
          </button>
          <button onclick="showBrokerageTab('activities')" id="tab-btn-activities"
            class="py-2 px-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700">
            Activities
          </button>
          <button onclick="showBrokerageTab('pnl')" id="tab-btn-pnl"
            class="py-2 px-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700">
            P&L Report
          </button>
        </nav>
      </div>

      <!-- Holdings Tab -->
      <div id="holdings-tab">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-4">
            <span class="text-sm text-gray-500">Show:</span>
            <label class="flex items-center gap-1 text-sm">
              <input type="checkbox" id="holdings-filter-options" checked onchange="applyHoldingsFilter()"
                class="rounded border-gray-300" />
              Options
            </label>
            <label class="flex items-center gap-1 text-sm">
              <input type="checkbox" id="holdings-filter-stocks" checked onchange="applyHoldingsFilter()"
                class="rounded border-gray-300" />
              Stocks
            </label>
          </div>
          <button onclick="loadHoldings()" class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
            Refresh
          </button>
        </div>
        <div id="holdings-container">
          <p class="text-sm text-gray-500">Click Refresh to load holdings</p>
        </div>
      </div>

      <!-- Activities Tab (hidden by default) -->
      <div id="activities-tab" class="hidden">
        <div class="flex flex-wrap items-center gap-4 mb-4">
          <input type="date" id="activities-start-date" value="${formatDate(thirtyDaysAgo)}"
            class="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          <span class="text-gray-400">to</span>
          <input type="date" id="activities-end-date" value="${formatDate(today)}"
            class="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          <button onclick="loadActivities()" class="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm">
            Load Activities
          </button>
        </div>
        <div class="flex items-center gap-4 mb-4">
          <span class="text-sm text-gray-500">Filter:</span>
          <label class="flex items-center gap-1 text-sm">
            <input type="checkbox" id="activities-filter-trades" checked onchange="applyActivitiesFilter()"
              class="rounded border-gray-300" />
            Trades
          </label>
          <label class="flex items-center gap-1 text-sm">
            <input type="checkbox" id="activities-filter-dividends" onchange="applyActivitiesFilter()"
              class="rounded border-gray-300" />
            Dividends
          </label>
          <label class="flex items-center gap-1 text-sm">
            <input type="checkbox" id="activities-filter-interest" onchange="applyActivitiesFilter()"
              class="rounded border-gray-300" />
            Interest
          </label>
          <label class="flex items-center gap-1 text-sm">
            <input type="checkbox" id="activities-filter-other" onchange="applyActivitiesFilter()"
              class="rounded border-gray-300" />
            Other
          </label>
        </div>
        <div id="activities-container">
          <p class="text-sm text-gray-500">Select a date range and click Load Activities</p>
        </div>
      </div>

      <!-- P&L Report Tab (hidden by default) -->
      <div id="pnl-tab" class="hidden">
        <div class="flex items-center gap-4 mb-4">
          <button onclick="loadPnlReport()" class="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm">
            Calculate P&L
          </button>
          <span class="text-sm text-gray-500">Analyzes all historical trades to match opening and closing positions</span>
        </div>
        <div id="pnl-container">
          <p class="text-sm text-gray-500">Click "Calculate P&L" to analyze your trades</p>
        </div>
      </div>
    </div>
  `);
}

export function showBrokerageTab(tab: 'holdings' | 'activities' | 'pnl'): void {
  const holdingsTab = document.getElementById('holdings-tab');
  const activitiesTab = document.getElementById('activities-tab');
  const pnlTab = document.getElementById('pnl-tab');
  const holdingsBtn = document.getElementById('tab-btn-holdings');
  const activitiesBtn = document.getElementById('tab-btn-activities');
  const pnlBtn = document.getElementById('tab-btn-pnl');

  // Hide all tabs
  holdingsTab?.classList.add('hidden');
  activitiesTab?.classList.add('hidden');
  pnlTab?.classList.add('hidden');

  // Reset all buttons
  [holdingsBtn, activitiesBtn, pnlBtn].forEach(btn => {
    btn?.classList.remove('border-gray-900');
    btn?.classList.add('border-transparent', 'text-gray-500');
  });

  // Show selected tab and highlight button
  if (tab === 'holdings') {
    holdingsTab?.classList.remove('hidden');
    holdingsBtn?.classList.add('border-gray-900');
    holdingsBtn?.classList.remove('border-transparent', 'text-gray-500');
  } else if (tab === 'activities') {
    activitiesTab?.classList.remove('hidden');
    activitiesBtn?.classList.add('border-gray-900');
    activitiesBtn?.classList.remove('border-transparent', 'text-gray-500');
  } else if (tab === 'pnl') {
    pnlTab?.classList.remove('hidden');
    pnlBtn?.classList.add('border-gray-900');
    pnlBtn?.classList.remove('border-transparent', 'text-gray-500');
  }
}

function renderActivitiesTable(activities: AccountActivity[]): void {
  const container = document.getElementById('activities-container');
  if (!container) return;

  if (activities.length === 0) {
    container.innerHTML = '<p class="text-sm text-gray-500">No activities found for this date range.</p>';
    return;
  }

  const rows = activities.map(activity => {
    const amountClass = (activity.amount ?? 0) >= 0 ? 'text-green-600' : 'text-red-600';
    const amountSign = (activity.amount ?? 0) >= 0 ? '+' : '';

    // Display symbol - prefer underlying for options, show option details in description
    const displaySymbol = activity.underlyingSymbol || activity.symbol || '—';

    // Build type display - include option type if present
    let typeDisplay: string = activity.type || '—';
    if (activity.optionType) {
      typeDisplay = activity.optionType.replace(/_/g, ' ');
    }

    // Format date
    const dateDisplay = activity.tradeDate
      ? new Date(activity.tradeDate).toLocaleDateString()
      : '—';

    return [
      dateDisplay,
      `<span class="font-medium">${typeDisplay}</span>`,
      `<span class="font-medium">${displaySymbol}</span>`,
      activity.units != null ? activity.units.toString() : '—',
      activity.price != null ? formatPriceWithCents(activity.price) : '—',
      activity.amount != null
        ? `<span class="${amountClass}">${amountSign}${formatPrice(activity.amount)}</span>`
        : '—',
      `<span class="text-xs text-gray-500">${activity.description || '—'}</span>`,
    ];
  });

  let html = `<p class="text-sm text-gray-500 mb-4">${activities.length} transactions</p>`;
  html += makeTable(
    ['Date', 'Type', 'Symbol', 'Units', 'Price', 'Amount', 'Description'],
    rows
  );

  container.innerHTML = html;
}

function renderHoldingsStrategies(strategies: HoldingsStrategy[]): void {
  const container = document.getElementById('holdings-container');
  if (!container) return;

  console.log(`📊 renderHoldingsStrategies called with ${strategies.length} strategies`);

  if (strategies.length === 0) {
    console.log('⚠️ No strategies to display - showing "No holdings found"');
    console.log('   cachedHoldings length:', cachedHoldings.length);
    container.innerHTML = '<p class="text-sm text-gray-500">No holdings found.</p>';
    return;
  }

  // Calculate totals across all strategies
  const totalMarketValue = strategies.reduce((sum, s) => sum + s.totalMarketValue, 0);
  const totalCostBasis = strategies.reduce((sum, s) => sum + s.totalCostBasis, 0);
  const totalUnrealizedPnl = strategies.reduce((sum, s) => sum + s.totalUnrealizedPnl, 0);
  const totalPnlPercent = totalCostBasis > 0 ? (totalUnrealizedPnl / totalCostBasis) * 100 : 0;

  const pnlClass = totalUnrealizedPnl >= 0 ? 'text-green-600' : 'text-red-600';
  const pnlSign = totalUnrealizedPnl >= 0 ? '+' : '';

  let html = `
    <div class="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
      <div class="bg-white p-4 rounded-lg border border-gray-200">
        <span class="text-sm text-gray-500">Total Market Value</span>
        <p class="text-xl font-bold">${formatPrice(totalMarketValue)}</p>
      </div>
      <div class="bg-white p-4 rounded-lg border border-gray-200">
        <span class="text-sm text-gray-500">Total Cost Basis</span>
        <p class="text-xl font-bold">${formatPrice(totalCostBasis)}</p>
      </div>
      <div class="bg-white p-4 rounded-lg border border-gray-200">
        <span class="text-sm text-gray-500">Unrealized P&L</span>
        <p class="text-xl font-bold ${pnlClass}">${pnlSign}${formatPrice(totalUnrealizedPnl)}</p>
      </div>
      <div class="bg-white p-4 rounded-lg border border-gray-200">
        <span class="text-sm text-gray-500">Return</span>
        <p class="text-xl font-bold ${pnlClass}">${pnlSign}${totalPnlPercent.toFixed(2)}%</p>
      </div>
    </div>

    <p class="text-sm text-gray-500 mb-4">${strategies.length} position${strategies.length !== 1 ? 's' : ''} (click to expand)</p>
  `;

  // Render each strategy as an expandable panel
  for (const strategy of strategies) {
    const strategyPnlClass = strategy.totalUnrealizedPnl >= 0 ? 'text-green-600' : 'text-red-600';
    const strategyPnlSign = strategy.totalUnrealizedPnl >= 0 ? '+' : '';

    // Format expiration
    const expDisplay = strategy.expiration
      ? new Date(strategy.expiration).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
      : '';

    // Build legs summary for header
    const legsSummary = strategy.legs.map(leg => {
      if (leg.parsed) {
        const dir = leg.direction === 'long' ? '+' : '-';
        return `${dir}${leg.quantity} $${leg.parsed.strike}${leg.parsed.type === 'CALL' ? 'C' : 'P'}`;
      } else if (strategy.isCash) {
        return formatPrice(leg.marketValue);
      } else {
        const dir = leg.direction === 'long' ? '+' : '-';
        return `${dir}${leg.quantity} shares`;
      }
    }).join(' / ');

    // Escape ID for use in HTML
    const safeId = strategy.id.replace(/[^a-zA-Z0-9-]/g, '_');

    html += `
      <div class="border border-gray-200 rounded-lg mb-3 bg-white overflow-hidden">
        <!-- Strategy Header (clickable) -->
        <div class="px-4 py-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
             onclick="toggleStrategy('${safeId}')">
          <div class="flex items-center gap-3">
            <svg id="strategy-arrow-${safeId}" class="w-4 h-4 text-gray-400 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
            <div>
              <div class="flex items-center gap-2">
                <span class="font-semibold">${strategy.underlying}</span>
                <span class="text-sm px-2 py-0.5 bg-gray-100 rounded">${strategy.strategyType}</span>
                ${expDisplay ? `<span class="text-xs text-gray-400">Exp ${expDisplay}</span>` : ''}
              </div>
              <div class="text-xs text-gray-500 mt-0.5">${legsSummary}</div>
            </div>
          </div>
          <div class="text-right">
            <div class="font-medium">${formatPrice(strategy.totalMarketValue)}</div>
            <div class="text-sm ${strategyPnlClass}">
              ${strategyPnlSign}${formatPrice(strategy.totalUnrealizedPnl)}
              <span class="text-xs">(${strategyPnlSign}${strategy.totalUnrealizedPnlPercent.toFixed(1)}%)</span>
            </div>
          </div>
        </div>

        <!-- Strategy Content (expandable) -->
        <div id="strategy-content-${safeId}" class="hidden border-t border-gray-100 bg-gray-50">
          <div class="p-4">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-gray-500 text-xs uppercase">
                  <th class="pb-2">Leg</th>
                  <th class="pb-2 text-right">Qty</th>
                  <th class="pb-2 text-right">Avg Cost</th>
                  <th class="pb-2 text-right">Price</th>
                  <th class="pb-2 text-right">Value</th>
                  <th class="pb-2 text-right">P&L</th>
                </tr>
              </thead>
              <tbody>
                ${strategy.legs.map(leg => {
                  const legPnlClass = leg.unrealizedPnl >= 0 ? 'text-green-600' : 'text-red-600';
                  const legPnlSign = leg.unrealizedPnl >= 0 ? '+' : '';
                  const legDesc = leg.parsed
                    ? `${leg.parsed.expirationDisplay} $${leg.parsed.strike} ${leg.parsed.type}`
                    : leg.holding.symbol;
                  const dirLabel = leg.direction === 'long' ? '' : '(short)';

                  return `
                    <tr class="border-t border-gray-200">
                      <td class="py-2">
                        <div class="font-medium">${legDesc}</div>
                        <div class="text-xs text-gray-400">${dirLabel}</div>
                      </td>
                      <td class="py-2 text-right">${leg.quantity}</td>
                      <td class="py-2 text-right">${formatPriceWithCents(leg.avgCost)}</td>
                      <td class="py-2 text-right">${formatPriceWithCents(leg.currentPrice)}</td>
                      <td class="py-2 text-right">${formatPrice(leg.marketValue)}</td>
                      <td class="py-2 text-right ${legPnlClass}">
                        ${legPnlSign}${formatPrice(leg.unrealizedPnl)}
                        <div class="text-xs">${legPnlSign}${leg.unrealizedPnlPercent.toFixed(1)}%</div>
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
              <tfoot>
                <tr class="border-t-2 border-gray-300 font-medium">
                  <td class="pt-2" colspan="4">Total</td>
                  <td class="pt-2 text-right">${formatPrice(strategy.totalMarketValue)}</td>
                  <td class="pt-2 text-right ${strategyPnlClass}">
                    ${strategyPnlSign}${formatPrice(strategy.totalUnrealizedPnl)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    `;
  }

  container.innerHTML = html;
}

// P&L Report Functions

export async function loadPnlReport(): Promise<void> {
  if (!connectionState.visitorId || !connectionState.selectedAccountId) return;

  const pnlContainer = document.getElementById('pnl-container');
  if (pnlContainer) {
    pnlContainer.innerHTML = '<div class="text-gray-400 text-sm">Loading all trades and calculating P&L...</div>';
  }

  try {
    // Load activities and cost basis overrides in parallel
    const [activities, costBasisOverrides] = await Promise.all([
      getActivities(
        connectionState.visitorId,
        connectionState.selectedAccountId,
        getPnlDateRange()
      ),
      getCostBasisOverrides(connectionState.selectedAccountId),
    ]);

    // Store for edit modal access
    cachedActivities = activities;
    currentCostBasisOverrides = costBasisOverrides;

    // Match trades with cost basis overrides applied
    const result = matchTrades(activities, costBasisOverrides, {
      currentHoldings: cachedHoldings.length > 0 ? cachedHoldings : undefined,
    });
    renderPnlReport(result, activities);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    if (pnlContainer) {
      pnlContainer.innerHTML = `<div class="text-red-600 text-sm">${message}</div>`;
    }
  }
}

interface TickerPnlGroup {
  underlying: string;
  strategies: OptionsStrategy[];
  totalPnl: number;
  totalDividends: number;
  openCount: number;
  closedCount: number;
  hasMissingCostBasis: boolean;
}

function renderPnlReport(result: TradeMatchingResult, activities: AccountActivity[]): void {
  const container = document.getElementById('pnl-container');
  if (!container) return;

  const { summary } = result;

  // Group into strategies, passing activities for dividend association
  const strategies = groupIntoStrategies(result, activities);

  // Group strategies by ticker
  const tickerGroups = new Map<string, TickerPnlGroup>();
  for (const strategy of strategies) {
    const existing = tickerGroups.get(strategy.underlying);
    if (existing) {
      existing.strategies.push(strategy);
      existing.totalPnl += strategy.totalPnl ?? 0;
      existing.totalDividends += strategy.totalDividends;
      if (strategy.isOpen) {
        existing.openCount++;
      } else {
        existing.closedCount++;
      }
      if (strategy.missingCostBasis) {
        existing.hasMissingCostBasis = true;
      }
    } else {
      tickerGroups.set(strategy.underlying, {
        underlying: strategy.underlying,
        strategies: [strategy],
        totalPnl: strategy.totalPnl ?? 0,
        totalDividends: strategy.totalDividends,
        openCount: strategy.isOpen ? 1 : 0,
        closedCount: strategy.isOpen ? 0 : 1,
        hasMissingCostBasis: !!strategy.missingCostBasis,
      });
    }
  }

  // Sort ticker groups by total P&L (descending)
  const sortedGroups = Array.from(tickerGroups.values()).sort((a, b) => b.totalPnl - a.totalPnl);

  // Summary stats
  const pnlClass = summary.totalRealizedPnl >= 0 ? 'text-green-600' : 'text-red-600';
  const pnlSign = summary.totalRealizedPnl >= 0 ? '+' : '';

  let html = `
    <div class="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
      <div class="bg-white p-4 rounded-lg border border-gray-200">
        <span class="text-sm text-gray-500">Total Realized P&L</span>
        <p class="text-2xl font-bold ${pnlClass}">${pnlSign}${formatPrice(summary.totalRealizedPnl)}</p>
      </div>
      <div class="bg-white p-4 rounded-lg border border-gray-200">
        <span class="text-sm text-gray-500">Win Rate</span>
        <p class="text-2xl font-bold">${summary.winRate.toFixed(1)}%</p>
        <span class="text-xs text-gray-400">${summary.totalWins}W / ${summary.totalLosses}L</span>
      </div>
      <div class="bg-white p-4 rounded-lg border border-gray-200">
        <span class="text-sm text-gray-500">Avg Win</span>
        <p class="text-xl font-semibold text-green-600">+${formatPrice(summary.averageWin)}</p>
      </div>
      <div class="bg-white p-4 rounded-lg border border-gray-200">
        <span class="text-sm text-gray-500">Avg Loss</span>
        <p class="text-xl font-semibold text-red-600">${formatPrice(summary.averageLoss)}</p>
      </div>
    </div>
  `;

  // Warning banner for trades with missing cost basis
  if (summary.tradesWithMissingCostBasis && summary.tradesWithMissingCostBasis > 0) {
    const unreliablePnlClass = (summary.unreliablePnl || 0) >= 0 ? 'text-green-600' : 'text-red-600';
    const unreliablePnlSign = (summary.unreliablePnl || 0) >= 0 ? '+' : '';
    html += `
      <div class="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div class="flex items-start gap-3">
          <span class="text-amber-500 text-xl">&#9888;</span>
          <div>
            <p class="font-medium text-amber-800">Missing Cost Basis Data</p>
            <p class="text-sm text-amber-700 mt-1">
              ${summary.tradesWithMissingCostBasis} trade${summary.tradesWithMissingCostBasis !== 1 ? 's have' : ' has'}
              missing cost basis (opened at $0, likely from account transfers).
              These trades show <span class="${unreliablePnlClass} font-semibold">${unreliablePnlSign}${formatPrice(summary.unreliablePnl || 0)}</span>
              P&L but are <strong>excluded</strong> from the total above.
            </p>
            <p class="text-xs text-amber-600 mt-2">
              To fix this, you may need to manually enter the original cost basis for transferred positions.
            </p>
          </div>
        </div>
      </div>
    `;
  }

  // Tickers list
  html += `<h4 class="text-lg font-medium mb-3">P&L by Symbol (${sortedGroups.length} tickers)</h4>`;
  html += '<div class="space-y-3">';

  for (const group of sortedGroups) {
    const groupPnlClass = group.totalPnl >= 0 ? 'text-green-600' : 'text-red-600';
    const groupPnlSign = group.totalPnl >= 0 ? '+' : '';
    const tickerDomId = domId(group.underlying);

    // Count badges
    const openBadge = group.openCount > 0
      ? `<span class="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">${group.openCount} open</span>`
      : '';
    const closedBadge = group.closedCount > 0
      ? `<span class="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">${group.closedCount} closed</span>`
      : '';
    const dividendBadge = group.totalDividends > 0
      ? `<span class="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">+${formatPrice(group.totalDividends)} div</span>`
      : '';
    const costBasisBadge = group.hasMissingCostBasis
      ? `<span class="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs" title="Some positions have missing cost basis">&#9888;</span>`
      : '';

    html += `
      <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div
          class="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
          onclick="togglePnlTicker(${jsString(group.underlying)})"
        >
          <div class="flex items-center gap-3">
            <span id="ticker-chevron-${tickerDomId}" class="text-gray-400 transition-transform text-lg">&#9654;</span>
            <span class="font-bold text-lg">${group.underlying}</span>
            ${costBasisBadge}
            <span class="text-sm text-gray-500">${group.strategies.length} trade${group.strategies.length !== 1 ? 's' : ''}</span>
            <div class="flex gap-2">
              ${openBadge}
              ${closedBadge}
              ${dividendBadge}
            </div>
          </div>
          <span class="${groupPnlClass} font-bold text-lg">${groupPnlSign}${formatPrice(group.totalPnl)}</span>
        </div>

        <div id="ticker-${tickerDomId}" data-underlying="${group.underlying}" class="hidden border-t border-gray-100">
          ${renderTickerStrategies(group.strategies)}
        </div>
      </div>
    `;
  }

  html += '</div>';
  container.innerHTML = html;
}

function renderTickerStrategies(strategies: OptionsStrategy[]): string {
  // Sort by date (most recent first)
  const sorted = [...strategies].sort((a, b) =>
    new Date(b.openDate).getTime() - new Date(a.openDate).getTime()
  );

  let html = '<div class="bg-gray-50">';

  for (const strategy of sorted) {
    const strategyDomId = domId(strategy.id);
    const pnlClass = (strategy.totalPnl ?? 0) >= 0 ? 'text-green-600' : 'text-red-600';
    const pnlSign = (strategy.totalPnl ?? 0) >= 0 ? '+' : '';

    // Build legs description
    const legsDesc = strategy.legs.map(leg => {
      const parsed = leg.parsed;
      if (!parsed) return strategy.underlying;

      const dir = leg.direction === 'long' ? '+' : '-';
      const strike = `$${parsed.strike}`;
      const type = parsed.type === 'CALL' ? 'C' : 'P';
      return `${dir}${leg.units} ${strike}${type}`;
    }).join(' / ');

    // Expiration display
    const expirations = [...new Set(strategy.legs.map(l => l.parsed?.expirationDisplay).filter(Boolean))];
    const expDisplay = expirations.join(' / ') || '—';

    // Days held
    let daysHeld = '';
    if (strategy.closeDate) {
      const days = Math.round(
        (new Date(strategy.closeDate).getTime() - new Date(strategy.openDate).getTime()) /
        (1000 * 60 * 60 * 24)
      );
      daysHeld = `${days}d`;
    }

    // Dividend indicator
    const hasDividends = strategy.dividends && strategy.dividends.length > 0;
    const dividendBadge = hasDividends
      ? `<span class="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">+${formatPrice(strategy.totalDividends)} div</span>`
      : '';

    // Status badge
    const statusBadge = strategy.isOpen
      ? '<span class="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">Open</span>'
      : '';

    // Missing cost basis warning badge with edit button
    let costBasisWarning = '';
    if (strategy.missingCostBasis) {
      // Find the leg(s) with missing cost basis to get activity IDs for editing
      const legsWithMissingCostBasis = strategy.legs.filter(l => l.missingCostBasis && l.openActivityId);
      const hasEditableLegs = legsWithMissingCostBasis.length > 0;

      costBasisWarning = `
        <span class="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs" title="P&L may be inaccurate - position has missing cost basis from transfer">&#9888; No Cost Basis</span>
        ${hasEditableLegs ? `<button onclick="event.stopPropagation(); editCostBasis('${legsWithMissingCostBasis[0].openActivityId}')" class="px-2 py-0.5 bg-amber-500 text-white rounded text-xs hover:bg-amber-600 ml-1">Edit</button>` : ''}
      `;
    }

    // Only make expandable if multi-leg or has dividends
    const isExpandable = strategy.legs.length > 1 || hasDividends;

    if (isExpandable) {
      // Expandable row for multi-leg trades
      html += `
        <div class="border-b border-gray-200 last:border-b-0">
          <div
            class="flex items-center justify-between p-3 pl-8 cursor-pointer hover:bg-gray-100"
            onclick="togglePnlStrategy(${jsString(strategy.id)})"
          >
            <div class="flex items-center gap-3">
              <span id="pnl-chevron-${strategyDomId}" class="text-gray-400 transition-transform text-sm">&#9654;</span>
              <div>
                <span class="text-sm font-medium">${strategy.strategyType}</span>
                ${statusBadge}
                ${costBasisWarning}
                ${dividendBadge}
              </div>
              <span class="text-xs text-gray-500">${legsDesc}</span>
              <span class="text-xs text-gray-400">Exp ${expDisplay}</span>
            </div>
            <div class="flex items-center gap-4 text-sm">
              ${daysHeld ? `<span class="text-gray-400">${daysHeld}</span>` : ''}
              ${strategy.totalPnl !== null
                ? `<span class="${pnlClass} font-medium">${pnlSign}${formatPrice(strategy.totalPnl)}</span>`
                : '<span class="text-gray-400">—</span>'}
            </div>
          </div>

          <div id="pnl-details-${strategyDomId}" data-strategy-id="${strategy.id}" class="hidden bg-white p-4 pl-12 border-t border-gray-100">
            <div class="text-xs text-gray-500 mb-2">
              Opened: ${new Date(strategy.openDate).toLocaleDateString()}
              ${strategy.closeDate ? ` | Closed: ${new Date(strategy.closeDate).toLocaleDateString()}` : ''}
            </div>

            <table class="w-full text-sm">
              <thead>
                <tr class="text-xs text-gray-500 uppercase">
                  <th class="text-left py-1">Leg</th>
                  <th class="text-right py-1">Qty</th>
                  <th class="text-right py-1">Open</th>
                  <th class="text-right py-1">Close</th>
                  <th class="text-right py-1">P&L</th>
                </tr>
              </thead>
              <tbody>
                ${strategy.legs.map(leg => {
                  const parsed = leg.parsed;
                  const legDesc = parsed
                    ? `${parsed.expirationDisplay} $${parsed.strike} ${parsed.type}`
                    : strategy.underlying;
                  const dirLabel = leg.direction === 'long' ? 'long' : 'short';
                  const legPnlClass = (leg.legPnl ?? 0) >= 0 ? 'text-green-600' : 'text-red-600';
                  const legPnlSign = (leg.legPnl ?? 0) >= 0 ? '+' : '';

                  return `
                    <tr class="border-t border-gray-200">
                      <td class="py-2">
                        <div class="font-medium">${legDesc}</div>
                        <div class="text-xs text-gray-400">(${dirLabel})</div>
                      </td>
                      <td class="py-2 text-right">${leg.units}</td>
                      <td class="py-2 text-right">${formatPriceWithCents(leg.openPrice)}</td>
                      <td class="py-2 text-right">${leg.closePrice !== null ? formatPriceWithCents(leg.closePrice) : '—'}</td>
                      <td class="py-2 text-right ${legPnlClass}">
                        ${leg.legPnl !== null ? `${legPnlSign}${formatPrice(leg.legPnl)}` : '—'}
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
              <tfoot>
                <tr class="border-t-2 border-gray-300 font-medium">
                  <td class="pt-2" colspan="4">Total</td>
                  <td class="pt-2 text-right ${pnlClass}">
                    ${strategy.totalPnl !== null ? `${pnlSign}${formatPrice(strategy.totalPnl)}` : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>

            ${hasDividends ? `
              <div class="mt-4 pt-3 border-t border-gray-200">
                <div class="text-xs text-gray-500 uppercase mb-2">Dividends Received</div>
                <table class="w-full text-sm">
                  <tbody>
                    ${strategy.dividends.map(div => `
                      <tr>
                        <td class="py-1 text-gray-600">${new Date(div.date).toLocaleDateString()}</td>
                        <td class="py-1 text-gray-500">${div.description}</td>
                        <td class="py-1 text-right text-green-600">+${formatPrice(div.amount)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                  <tfoot>
                    <tr class="border-t border-gray-200 font-medium">
                      <td colspan="2" class="pt-2">Total Dividends</td>
                      <td class="pt-2 text-right text-green-600">+${formatPrice(strategy.totalDividends)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ` : ''}
          </div>
        </div>
      `;
    } else {
      // Simple non-expandable row for single-leg trades
      const leg = strategy.legs[0];
      const dirLabel = leg.direction === 'long' ? 'long' : 'short';

      html += `
        <div class="border-b border-gray-200 last:border-b-0">
          <div class="flex items-center justify-between p-3 pl-8">
            <div class="flex items-center gap-3">
              <span class="w-4"></span>
              <div>
                <span class="text-sm font-medium">${strategy.strategyType}</span>
                <span class="text-xs text-gray-400 ml-1">(${dirLabel})</span>
                ${statusBadge}
                ${costBasisWarning}
              </div>
              <span class="text-xs text-gray-500">${legsDesc}</span>
              <span class="text-xs text-gray-400">Exp ${expDisplay}</span>
              <span class="text-xs text-gray-400">${leg.units} @ ${formatPriceWithCents(leg.openPrice)}${leg.closePrice !== null ? ` → ${formatPriceWithCents(leg.closePrice)}` : ''}</span>
            </div>
            <div class="flex items-center gap-4 text-sm">
              ${daysHeld ? `<span class="text-gray-400">${daysHeld}</span>` : ''}
              ${strategy.totalPnl !== null
                ? `<span class="${pnlClass} font-medium">${pnlSign}${formatPrice(strategy.totalPnl)}</span>`
                : '<span class="text-gray-400">—</span>'}
            </div>
          </div>
        </div>
      `;
    }
  }

  html += '</div>';
  return html;
}

export function togglePnlTicker(underlying: string): void {
  const tickerDomId = domId(underlying);
  const details = document.getElementById(`ticker-${tickerDomId}`);
  const chevron = document.getElementById(`ticker-chevron-${tickerDomId}`);

  if (details && chevron) {
    const isHidden = details.classList.contains('hidden');
    details.classList.toggle('hidden');
    chevron.style.transform = isHidden ? 'rotate(90deg)' : 'rotate(0deg)';
  }
}

export function togglePnlStrategy(strategyId: string): void {
  const strategyDomId = domId(strategyId);
  const details = document.getElementById(`pnl-details-${strategyDomId}`);
  const chevron = document.getElementById(`pnl-chevron-${strategyDomId}`);

  if (details && chevron) {
    const isHidden = details.classList.contains('hidden');
    details.classList.toggle('hidden');
    chevron.style.transform = isHidden ? 'rotate(90deg)' : 'rotate(0deg)';
  }
}

// ============================================================================
// Cost Basis Edit Modal
// ============================================================================

/**
 * Open the cost basis edit modal for a specific activity
 */
export function editCostBasis(activityId: string): void {
  // Find the activity in our cached activities
  const activity = cachedActivities.find(a => a.id === activityId);
  if (!activity) {
    console.error('Activity not found:', activityId);
    return;
  }

  costBasisModalActivity = activity;

  // Check if there's an existing override
  const existingOverride = currentCostBasisOverrides.get(activityId);

  const isEditing = !!existingOverride;
  const modalTitle = isEditing ? 'Edit Cost Basis' : 'Enter Cost Basis';

  const modal = document.createElement('div');
  modal.id = 'cost-basis-modal';
  modal.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
  modal.innerHTML = `
    <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
      <h3 class="text-lg font-bold mb-4">${modalTitle}</h3>

      <div class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="text-sm text-gray-500">Symbol</label>
            <p class="font-medium">${activity.symbol || activity.underlyingSymbol || 'Unknown'}</p>
          </div>
          <div>
            <label class="text-sm text-gray-500">Trade Date</label>
            <p class="font-medium">${formatDate(new Date(activity.tradeDate))}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="text-sm text-gray-500">Units</label>
            <p class="font-medium">${Math.abs(activity.units || 0)}</p>
          </div>
          <div>
            <label class="text-sm text-gray-500">Type</label>
            <p class="font-medium">${activity.type}</p>
          </div>
        </div>

        <div>
          <label class="text-sm text-gray-500 block mb-1">Cost Per Share ($)</label>
          <input type="number" id="cost-basis-input" step="0.01" min="0"
            class="w-full border border-gray-300 rounded p-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            placeholder="e.g., 150.25"
            value="${existingOverride?.price ?? ''}">
        </div>

        <div>
          <label class="text-sm text-gray-500 block mb-1">Notes (optional)</label>
          <input type="text" id="cost-basis-notes"
            class="w-full border border-gray-300 rounded p-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            placeholder="e.g., Transferred from old account"
            value="${existingOverride?.notes ?? ''}">
        </div>
      </div>

      <div class="flex justify-end gap-3 mt-6">
        <button onclick="closeCostBasisModal()"
          class="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors">Cancel</button>
        <button onclick="saveCostBasisFromModal()"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">Save</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // Focus the input
  setTimeout(() => {
    const input = document.getElementById('cost-basis-input') as HTMLInputElement;
    input?.focus();
  }, 100);
}

/**
 * Close the cost basis modal
 */
export function closeCostBasisModal(): void {
  const modal = document.getElementById('cost-basis-modal');
  if (modal) {
    modal.remove();
  }
  costBasisModalActivity = null;
}

/**
 * Capture the current expanded state of the P&L report UI
 */
function capturePnlExpandedState(): { tickers: string[]; strategies: string[] } {
  const expandedTickers: string[] = [];
  const expandedStrategies: string[] = [];

  // Find all expanded tickers (elements that don't have 'hidden' class)
  document.querySelectorAll('[id^="ticker-"]').forEach(el => {
    if (!el.classList.contains('hidden')) {
      const underlying = (el as HTMLElement).dataset.underlying || '';
      if (underlying) {
        expandedTickers.push(underlying);
      }
    }
  });

  // Find all expanded strategies
  document.querySelectorAll('[id^="pnl-details-"]').forEach(el => {
    if (!el.classList.contains('hidden')) {
      const strategyId = (el as HTMLElement).dataset.strategyId || '';
      if (strategyId) {
        expandedStrategies.push(strategyId);
      }
    }
  });

  return { tickers: expandedTickers, strategies: expandedStrategies };
}

/**
 * Restore the expanded state of the P&L report UI
 */
function restorePnlExpandedState(state: { tickers: string[]; strategies: string[] }): void {
  // Restore expanded tickers
  for (const underlying of state.tickers) {
    const details = document.getElementById(`ticker-${domId(underlying)}`);
    if (details && details.classList.contains('hidden')) {
      togglePnlTicker(underlying);
    }
  }

  // Restore expanded strategies
  for (const strategyId of state.strategies) {
    const details = document.getElementById(`pnl-details-${domId(strategyId)}`);
    if (details && details.classList.contains('hidden')) {
      togglePnlStrategy(strategyId);
    }
  }
}

/**
 * Save the cost basis from the modal
 */
export async function saveCostBasisFromModal(): Promise<void> {
  if (!costBasisModalActivity || !connectionState.selectedAccountId) {
    return;
  }

  const input = document.getElementById('cost-basis-input') as HTMLInputElement;
  const notesInput = document.getElementById('cost-basis-notes') as HTMLInputElement;

  const price = parseFloat(input?.value || '');
  const notes = notesInput?.value?.trim() || null;

  if (isNaN(price) || price < 0) {
    alert('Please enter a valid cost per share (must be 0 or greater)');
    return;
  }

  // Capture expanded state before refresh
  const expandedState = capturePnlExpandedState();

  // Disable the save button to prevent double-clicks
  const saveBtn = document.querySelector('#cost-basis-modal button:last-child') as HTMLButtonElement;
  if (saveBtn) {
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving...';
  }

  const success = await saveCostBasisOverride(connectionState.selectedAccountId, {
    activityId: costBasisModalActivity.id,
    price,
    symbol: costBasisModalActivity.symbol || costBasisModalActivity.underlyingSymbol || '',
    units: Math.abs(costBasisModalActivity.units || 0),
    tradeDate: costBasisModalActivity.tradeDate,
    notes,
  });

  if (success) {
    console.log(`Saved cost basis: $${price} for activity ${costBasisModalActivity.id}`);
    closeCostBasisModal();

    // Reload the P&L report to reflect the new cost basis
    await loadPnlReport();

    // Restore expanded state after render
    restorePnlExpandedState(expandedState);
  } else {
    alert('Failed to save cost basis. Please try again.');
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save';
    }
  }
}
